import { apiDownload, apiFetch, type DownloadedFile } from '@/api/client'

export const ImportOutcome = {
  CREATED: 'CREATED',
  UPDATED: 'UPDATED',
  SKIPPED: 'SKIPPED',
} as const

export type ImportOutcome = (typeof ImportOutcome)[keyof typeof ImportOutcome]

/** Why a customer here may be the company a row of an import describes. */
export const DuplicateReason = {
  SAME_NAME: 'SAME_NAME',
  SIMILAR_NAME: 'SIMILAR_NAME',
  EMAIL_DOMAIN: 'EMAIL_DOMAIN',
  WEBSITE: 'WEBSITE',
  PHONE: 'PHONE',
  ADDRESS: 'ADDRESS',
} as const

export type DuplicateReason = (typeof DuplicateReason)[keyof typeof DuplicateReason]

/** A customer here that a company the preview would create may already be. */
export type DuplicateCandidate = {
  customerId: string
  companyName: string
  customerNumber: string | null
  city: string | null
  reasons: DuplicateReason[]
}

/** @param line the line in the file as Excel numbers it, so somebody can go and look at it. */
export type ImportResult = {
  line: number
  companyName: string
  outcome: ImportOutcome
  /** Why a row was skipped, or what is worth knowing about one that was not. */
  detail: string | null
  /** Only on a preview, only for a company it would create: whom it may already be, likeliest first. */
  candidates: DuplicateCandidate[]
}

export type ImportProblem = { line: number; message: string }

/**
 * What an import did, or would do.
 *
 * `applied` is false for a dry run **and** for a refused file; `problems` is what tells the two
 * apart. A refused file wrote nothing at all — one unreadable row stops the lot, so an import is
 * never half applied.
 */
export type ImportReport = {
  dryRun: boolean
  applied: boolean
  created: number
  updated: number
  skipped: number
  results: ImportResult[]
  problems: ImportProblem[]
}

/**
 * Administrators only, both ways — see CustomerTransferController.
 *
 * @param merges the line of a company the preview would have created, to the customer it is to be
 *   merged into instead. Keyed by line, which is stable as long as the file is the same one.
 */
export function importCustomers(
  file: File,
  dryRun: boolean,
  merges: Record<number, string> = {},
): Promise<ImportReport> {
  const body = new FormData()
  body.append('file', file)
  const chosen = Object.entries(merges).map(([line, customerId]) => ({ line: Number(line), customerId }))
  if (chosen.length > 0) {
    // A JSON part of its own, typed so the backend reads it as JSON rather than as a form field.
    body.append('merges', new Blob([JSON.stringify(chosen)], { type: 'application/json' }))
  }
  // On the query string rather than in the form: the flag decides whether anything is written, and
  // it should be visible in an access log next to the request that did it.
  return apiFetch<ImportReport>(`/api/customers/import?dryRun=${dryRun}`, { method: 'POST', body })
}

/**
 * The customers and contacts Odoo does not have yet, as a file for Odoo's own import — not for
 * Excel. Every row carries an external id made from the record's id here, by which the next import
 * out of Odoo knows it again. The name the backend stamped with the day it was taken.
 */
export function exportCustomersForOdoo(): Promise<DownloadedFile> {
  return apiDownload('/api/customers/export/odoo')
}

/** A file with a header and nothing under it: nothing that Odoo does not already have. */
export async function holdsNoRows(blob: Blob): Promise<boolean> {
  return (await blob.text()).trim().split(/\r?\n/).length <= 1
}

/**
 * Hands the browser a file to save.
 *
 * The object URL is revoked on the next frame rather than immediately: Safari has not begun
 * reading it when `click()` returns, and revoking too early leaves the user with a failed
 * download and no error.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.append(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 0)
}
